/**
 * Token Refresh Utility
 * Implements automatic JWT token refresh mechanism
 * CRITICAL Security Fix: Auto-refresh tokens before expiry
 */

import { getEffectiveApiBaseUrl, log } from "../config/api.config";

const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";

const MIN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes
const MAX_REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes

let refreshTimer: number | null = null;
let isRefreshing = false;

/**
 * Decode JWT token to get expiry time
 */
export function decodeToken(token: string): { exp: number } | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    log.error("Failed to decode token:", error);
    return null;
  }
}

/**
 * Check if token needs refresh
 */
export function shouldRefreshToken(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded?.exp) return false;

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;

  return timeUntilExpiry <= 5 * 60 * 1000 && timeUntilExpiry > 0;
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded?.exp) return true;

  const expiryTime = decoded.exp * 1000;
  return Date.now() >= expiryTime;
}

/**
 * Refresh the JWT token
 */
export async function requestTokenRefresh(): Promise<{
  accessToken: string;
  refreshToken?: string;
} | null> {
  if (isRefreshing) {
    log.debug("Token refresh already in progress");
    return null;
  }

  try {
    isRefreshing = true;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      log.warn("No refresh token available");
      return null;
    }

    const response = await fetch(`${getEffectiveApiBaseUrl()}/v1/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    const data = await response.json();
    const accessToken =
      data?.data?.accessToken ||
      data?.data?.access_token ||
      data?.accessToken ||
      data?.access_token ||
      data?.data?.token ||
      data?.token;
    const newRefreshToken =
      data?.data?.refreshToken || data?.refreshToken || data?.data?.refresh_token;

    if (accessToken) {
      log.info("✅ Token refreshed successfully");
      return { accessToken, refreshToken: newRefreshToken };
    }

    throw new Error("No access token in refresh response");
  } catch (error) {
    log.error("❌ Token refresh failed:", error);
    return null;
  } finally {
    isRefreshing = false;
  }
}

export async function refreshToken(): Promise<string | null> {
  const result = await requestTokenRefresh();
  if (!result?.accessToken) {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    stopTokenRefreshMonitoring();
    window.dispatchEvent(
      new CustomEvent("unauthorized", {
        detail: { message: "Session expired. Please login again." },
      })
    );
    return null;
  }

  localStorage.setItem(ACCESS_TOKEN_KEY, result.accessToken);
  if (result.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, result.refreshToken);
  }

  return result.accessToken;
}

/**
 * Start automatic token refresh monitoring
 */
export function startTokenRefreshMonitoring() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshTokenValue) {
    log.debug("No refresh token found, skipping refresh monitoring");
    return;
  }

  const scheduleNext = () => {
    const delay =
      MIN_REFRESH_INTERVAL +
      Math.floor(Math.random() * (MAX_REFRESH_INTERVAL - MIN_REFRESH_INTERVAL));
    refreshTimer = window.setTimeout(async () => {
      const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!currentRefreshToken) {
        log.debug("Refresh token missing, stopping refresh monitoring");
        stopTokenRefreshMonitoring();
        return;
      }

      await refreshToken();
      scheduleNext();
    }, delay);
  };

  scheduleNext();
  log.info("✅ Token refresh monitoring started");
}

/**
 * Stop automatic token refresh monitoring
 */
export function stopTokenRefreshMonitoring() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
    log.info("Token refresh monitoring stopped");
  }
}

/**
 * Get time until token expiry (in milliseconds)
 */
export function getTokenExpiryTime(token: string): number | null {
  const decoded = decodeToken(token);
  if (!decoded?.exp) return null;

  const expiryTime = decoded.exp * 1000;
  return Math.max(0, expiryTime - Date.now());
}
